import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { collection, query, getDocs, doc, updateDoc, where, addDoc, Timestamp, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { sendTrainerAssignmentEmail } from "../services/emailService";
import "./matchup.css";

/**
 * Matchup Page
 * - Shows list of appointment matchup requests
 * - Allows users to match appointments with available agents
 */

// Demo/Sample Data
const DEMO_REQUESTS = [
  // {
  //   id: 'demo-1',
  //   clientName: 'Sarah Johnson',
  //   appointmentType: 'Life Insurance Consultation',
  //   requestDate: new Date(2025, 9, 10),
  //   preferredDate: 'Oct 15-20',
  //   preferredTime: '2:00 PM - 5:00 PM',
  //   duration: 60,
  //   status: 'pending',
  //   assignedAgent: null,
  //   notes: 'Client is interested in term life insurance. Has two young children and wants to ensure their future is protected. Prefers afternoon meetings.',
  //   priority: 'urgent',
  //   contactInfo: { email: 'sarah.j@email.com', phone: '(555) 123-4567' },
  //   createdBy: 'System',
  //   createdAt: new Date(2025, 9, 8)
  // },
  // {
  //   id: 'demo-2',
  //   clientName: 'Michael Chen',
  //   appointmentType: 'Retirement Planning',
  //   requestDate: new Date(2025, 9, 11),
  //   preferredDate: 'Oct 18',
  //   preferredTime: '10:00 AM - 12:00 PM',
  //   duration: 45,
  //   status: 'matched',
  //   assignedAgent: 'David Martinez',
  //   notes: 'Client is 55 years old and looking to plan for retirement in the next 10 years. Has existing 401k and wants to maximize returns.',
  //   priority: 'high',
  //   contactInfo: { email: 'm.chen@email.com', phone: '(555) 234-5678' },
  //   createdBy: 'Admin',
  //   createdAt: new Date(2025, 9, 7)
  // },
  // {
  //   id: 'demo-3',
  //   clientName: 'Emily Rodriguez',
  //   appointmentType: 'Investment Strategy',
  //   requestDate: new Date(2025, 9, 9),
  //   preferredDate: 'Oct 22',
  //   preferredTime: '1:00 PM - 3:00 PM',
  //   duration: 90,
  //   status: 'pending',
  //   assignedAgent: null,
  //   notes: 'High-net-worth individual looking to diversify investment portfolio. Interested in alternative investments and tax strategies.',
  //   priority: 'high',
  //   contactInfo: { email: 'e.rodriguez@email.com', phone: '(555) 345-6789' },
  //   createdBy: 'System',
  //   createdAt: new Date(2025, 9, 6)
  // },
  // {
  //   id: 'demo-4',
  //   clientName: 'Robert Thompson',
  //   appointmentType: 'Estate Planning',
  //   requestDate: new Date(2025, 9, 12),
  //   preferredDate: 'Oct 25',
  //   preferredTime: '9:00 AM - 11:00 AM',
  //   duration: 120,
  //   status: 'completed',
  //   assignedAgent: 'Lisa Wang',
  //   notes: 'Elderly client (78) needs comprehensive estate planning including will, trust, and power of attorney. Family meeting required.',
  //   priority: 'urgent',
  //   contactInfo: { email: 'r.thompson@email.com', phone: '(555) 456-7890' },
  //   createdBy: 'Admin',
  //   createdAt: new Date(2025, 9, 5)
  // },
  // {
  //   id: 'demo-5',
  //   clientName: 'Jennifer Davis',
  //   appointmentType: 'Health Insurance Review',
  //   requestDate: new Date(2025, 9, 13),
  //   preferredDate: 'Oct 30',
  //   preferredTime: '3:00 PM - 4:00 PM',
  //   duration: 30,
  //   status: 'pending',
  //   assignedAgent: null,
  //   notes: 'Annual health insurance review. Client has been with us for 3 years and is generally satisfied with current coverage.',
  //   priority: 'normal',
  //   contactInfo: { email: 'j.davis@email.com', phone: '(555) 567-8901' },
  //   createdBy: 'System',
  //   createdAt: new Date(2025, 9, 11)
  // },
  // {
  //   id: 'demo-6',
  //   clientName: 'James Peterson',
  //   appointmentType: 'Business Insurance',
  //   requestDate: new Date(2025, 9, 8),
  //   preferredDate: 'Oct 28',
  //   preferredTime: '3:00 PM',
  //   duration: 60,
  //   status: 'pending',
  //   assignedAgent: null,
  //   notes: 'Small business owner (tech startup) looking for comprehensive business insurance package. Needs coverage for 15 employees.',
  //   priority: 'high',
  //   contactInfo: { email: 'james.p@techstartup.com', phone: '(555) 678-9012' },
  //   createdBy: 'Admin',
  //   createdAt: new Date(2025, 9, 9)
  // },
  // {
  //   id: 'demo-7',
  //   clientName: 'Maria Garcia',
  //   appointmentType: 'College Savings Plan',
  //   requestDate: new Date(2025, 9, 10),
  //   preferredDate: 'Flexible',
  //   preferredTime: 'Evenings preferred',
  //   duration: 30,
  //   status: 'pending',
  //   assignedAgent: null,
  //   notes: 'Parents of newborn wanting to start college savings plan. First-time planning session.',
  //   priority: 'low',
  //   contactInfo: { email: 'm.garcia@email.com', phone: '(555) 789-0123' },
  //   createdBy: 'System',
  //   createdAt: new Date(2025, 9, 10)
  // },
  // {
  //   id: 'demo-8',
  //   clientName: 'David Lee',
  //   appointmentType: 'Mortgage Protection',
  //   requestDate: new Date(2025, 9, 8),
  //   preferredDate: 'Oct 20',
  //   preferredTime: '11:00 AM',
  //   duration: 45,
  //   status: 'completed',
  //   assignedAgent: 'Mark Johnson',
  //   notes: 'Just purchased first home and needs mortgage protection insurance. Very satisfied with the consultation.',
  //   priority: 'normal',
  //   contactInfo: { email: 'd.lee@email.com', phone: '(555) 890-1234' },
  //   createdBy: 'System',
  //   createdAt: new Date(2025, 9, 4)
  // }
];

// Simple agent list for autocomplete
const AGENT_OPTIONS = [
  "David Martinez",
  "Lisa Wang",
  "Mark Johnson",
  "Sarah Chen",
  "Mike Rodriguez",
];

export default function MatchupPage() {
  const { user } = useAuth();
  const [matchupRequests, setMatchupRequests] = useState(DEMO_REQUESTS);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'pending' | 'matched' | 'completed'
  const [appointmentTypeFilter, setAppointmentTypeFilter] = useState("all"); // 'all' | appointment types
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Track selected date { month, year, day }
  const [showNewMatchupModal, setShowNewMatchupModal] = useState(false);
  const [editingMatchup, setEditingMatchup] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentSearchOpen, setAgentSearchOpen] = useState(false);
  const [agentSearchRequestId, setAgentSearchRequestId] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpRequest, setFollowUpRequest] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [showProspectDropdown, setShowProspectDropdown] = useState(false);
  const [selectedProspectId, setSelectedProspectId] = useState(null);
  const [filteredTrainees, setFilteredTrainees] = useState([]);
  const [showTraineeDropdown, setShowTraineeDropdown] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({
    appointmentHappened: null,
    // Fields for YES
    fnaTaken: null,
    fnaWhyNot: '',
    secondAppointmentScheduled: null,
    referrals: null,
    friendBecamePartner: null,
    traineeEdified: null, // 'yes', 'no', 'kinda'
    madeSale: null,
    notesYes: '',
    // Fields for NO
    reason: '',
    notesNo: '',
    scheduleAnother: false
  });
  
  // Current month/year state (default to current date)
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const [newMatchupForm, setNewMatchupForm] = useState({
    clientName: '',
    appointmentType: 'Consultation',
    timezone: 'America/New_York',
    preferredDate: '',
    preferredTime: '',
    duration: 60,
    quadrant: 'Not Urgent/Important',
    meetingType: 'URL',
    meetingUrl: '',
    meetingAddress: '',
    meetingCity: '',
    meetingState: '',
    meetingZip: '',
    notes: '',
    prospectEmail: '',
    prospectPhone: '',
    traineeName: localStorage.getItem('wb.name') || '',
    traineeEmail: localStorage.getItem('wb.email') || '',
    traineeId: localStorage.getItem('wb.userId') || '',
    assignedAgent: '',
    assignedAgentId: '',
    profileFlags: {
      age25Plus: false,
      married: false,
      dependentChildren: false,
      homeowner: false,
      solidCareer: false,
      income75kPlus: false,
      dissatisfied: false,
      entrepreneurial: false,
      spanishPreferred: false,
    }
  });

  // Helper functions for month navigation
  const getMonthName = (monthIndex) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthIndex];
  };

  const dayLabels = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const goToPreviousMonth = () => {
    setCurrentMonth((prevMonth) => {
      const nextMonth = prevMonth === 0 ? 11 : prevMonth - 1;
      setCurrentYear((prevYear) => (prevMonth === 0 ? prevYear - 1 : prevYear));
      return nextMonth;
    });
    setSelectedDate(null); // Clear selection when changing months
  };

  const goToNextMonth = () => {
    setCurrentMonth((prevMonth) => {
      const nextMonth = prevMonth === 11 ? 0 : prevMonth + 1;
      setCurrentYear((prevYear) => (prevMonth === 11 ? prevYear + 1 : prevYear));
      return nextMonth;
    });
    setSelectedDate(null); // Clear selection when changing months
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    // Use UTC to avoid timezone edge cases when computing the starting weekday
    return new Date(Date.UTC(year, month, 1)).getUTCDay();
  };

  // Fetch prospects from Firestore
  useEffect(() => {
    const fetchProspects = async () => {
      try {
        const prospectsRef = collection(db, "prospects");
        const q = query(prospectsRef, where("status", "!=", "inactive"));
        const querySnapshot = await getDocs(q);
        const prospectsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProspects(prospectsData);
      } catch (error) {
        console.error('Error fetching prospects:', error);
      }
    };
    fetchProspects();
  }, []);

  // Load matchup requests from Firebase (appointments with matchUp flag)
  useEffect(() => {
    const loadMatchupRequests = async () => {
      if (!user) {
        setLoading(false);
        // Keep demo data if no user
        return;
      }

      try {
        setLoading(true);
        // Load appointments with matchUp flag = true
        const appointmentsRef = collection(db, 'appointments');
        const q = query(appointmentsRef, where('matchUp', '==', true));
        const querySnapshot = await getDocs(q);
        
        const requests = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('📦 Initial load - Raw database data for', doc.id, ':', data);
          // Determine status based on trainer assignment and approval
          let status = 'new';
          if (data.assignedAgent && data.assignedAgentId) {
            status = data.trainerApproved ? 'matched' : 'pending';
          }
          if (data.completed) {
            status = 'completed';
          }
          return {
            id: doc.id,
            clientName: data.prospectName || data.title || 'Unknown Client',
            appointmentType: data.type || 'Consultation',
            requestDate: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            preferredDate: data.dateTime?.toDate ? data.dateTime.toDate().toLocaleDateString() : 'TBD',
            preferredTime: data.dateTime?.toDate ? data.dateTime.toDate().toLocaleTimeString() : 'TBD',
            dateTime: data.dateTime?.toDate ? data.dateTime.toDate() : null,
            duration: data.duration || 60,
            status: status,
            assignedAgent: data.assignedAgent || null,
            assignedAgentId: data.assignedAgentId || null,
            trainerApproved: data.trainerApproved || false,
            trainerApprovedAt: data.trainerApprovedAt?.toDate ? data.trainerApprovedAt.toDate() : null,
            notes: data.notes || '',
            quadrant: data.quadrant || 'Not Urgent/Important',
            timezone: data.timezone || 'America/New_York',
            meetingType: data.meetingType || 'URL',
            meetingUrl: data.meetingUrl || '',
            meetingAddress: data.meetingAddress || '',
            meetingCity: data.meetingCity || '',
            meetingState: data.meetingState || '',
            meetingZip: data.meetingZip || '',
            contactInfo: {
              email: data.prospectEmail || '',
              phone: data.prospectPhone || ''
            },
            prospectEmail: data.prospectEmail || '',
            prospectPhone: data.prospectPhone || '',
            traineeName: data.traineeName || data.createdByName || 'System',
            traineeEmail: data.traineeEmail || '',
            traineeId: data.traineeId || data.createdBy || '',
            createdBy: data.createdByName || 'System',
            createdByName: data.createdByName || 'System',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            prospectId: data.prospectId || null,
            profileFlags: data.profileFlags || {
              age25Plus: false,
              married: false,
              dependentChildren: false,
              homeowner: false,
              solidCareer: false,
              income75kPlus: false,
              dissatisfied: false,
              entrepreneurial: false,
              spanishPreferred: false,
            },
            // Follow-up fields grouped under followUp
            followUp: {
              appointmentHappened:
                data.followUp?.appointmentHappened ?? data.appointmentHappened ?? null,
              fnaTaken: data.followUp?.fnaTaken ?? data.fnaTaken ?? null,
              fnaWhyNot: data.followUp?.fnaWhyNot ?? data.fnaWhyNot ?? "",
              secondAppointmentScheduled:
                data.followUp?.secondAppointmentScheduled ??
                data.secondAppointmentScheduled ??
                null,
              referrals: data.followUp?.referrals ?? data.referrals ?? null,
              friendBecamePartner:
                data.followUp?.friendBecamePartner ?? data.friendBecamePartner ?? null,
              traineeEdified: data.followUp?.traineeEdified ?? data.traineeEdified ?? null,
              madeSale: data.followUp?.madeSale ?? data.madeSale ?? null,
              followUpNotes: data.followUp?.followUpNotes ?? data.followUpNotes ?? "",
              noShowReason: data.followUp?.noShowReason ?? data.noShowReason ?? "",
              scheduleAnother: data.followUp?.scheduleAnother ?? data.scheduleAnother ?? false,
              followUpCompleted:
                data.followUp?.followUpCompleted ?? data.followUpCompleted ?? false,
            },
          };
        });

        // Merge demo data with Firebase data
        const allRequests = [...DEMO_REQUESTS, ...requests]
          .sort((a, b) => {
            // Sort by date
            return a.requestDate - b.requestDate;
          });

        setMatchupRequests(allRequests);
      } catch (error) {
        console.error('Error loading matchup requests:', error);
        // Keep demo data on error
      } finally {
        setLoading(false);
      }

    };

    loadMatchupRequests();
  }, [user]);

  // Load agents from users collection for autocomplete
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const list = snap.docs
          .map((d) => {
            const data = d.data();
            const name = data?.name?.trim();
            const email = data?.email?.trim();
            const label = name || email || d.id;
            return label ? { id: d.id, name, email, label } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setAgents(list);
      } catch (err) {
        console.error("Error loading agents:", err);
      }
    };

    loadAgents();
  }, []);

  const agentOptions = useMemo(() => {
    const baseList =
      agents.length > 0
        ? agents
        : AGENT_OPTIONS.map((name) => ({ id: name, label: name }));

    const dedup = new Map();
    baseList.forEach((agent) => {
      const label = agent.label || agent.name || agent.email || agent.id;
      if (!label) return;
      const id = agent.id || label;
      if (!dedup.has(id)) {
        dedup.set(id, { ...agent, id, label });
      }
    });

    return Array.from(dedup.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [agents]);

  const onRefresh = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      // Load appointments with matchUp flag = true
      const appointmentsRef = collection(db, 'appointments');
      const q = query(appointmentsRef, where('matchUp', '==', true));
      const querySnapshot = await getDocs(q);
      
      const requests = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📦 Raw database data for', doc.id, ':', data);
        // Determine status based on trainer assignment and approval
        let status = 'new';
        if (data.assignedAgent && data.assignedAgentId) {
          status = data.trainerApproved ? 'matched' : 'pending';
        }
        if (data.completed) {
          status = 'completed';
        }
        return {
          id: doc.id,
          clientName: data.prospectName || data.title || 'Unknown Client',
          appointmentType: data.type || 'Consultation',
          requestDate: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          preferredDate: data.dateTime?.toDate ? data.dateTime.toDate().toLocaleDateString() : 'TBD',
          preferredTime: data.dateTime?.toDate ? data.dateTime.toDate().toLocaleTimeString() : 'TBD',
          dateTime: data.dateTime?.toDate ? data.dateTime.toDate() : null,
          duration: data.duration || 60,
          status: status,
          assignedAgent: data.assignedAgent || null,
          assignedAgentId: data.assignedAgentId || null,
          trainerApproved: data.trainerApproved || false,
          trainerApprovedAt: data.trainerApprovedAt?.toDate ? data.trainerApprovedAt.toDate() : null,
          notes: data.notes || '',
          quadrant: data.quadrant || 'Not Urgent/Important',
          timezone: data.timezone || 'America/New_York',
          meetingType: data.meetingType || 'URL',
          meetingUrl: data.meetingUrl || '',
          meetingAddress: data.meetingAddress || '',
          meetingCity: data.meetingCity || '',
          meetingState: data.meetingState || '',
          meetingZip: data.meetingZip || '',
          contactInfo: {
            email: data.prospectEmail || '',
            phone: data.prospectPhone || ''
          },
          prospectEmail: data.prospectEmail || '',
          prospectPhone: data.prospectPhone || '',
          traineeName: data.traineeName || data.createdByName || 'System',
          traineeEmail: data.traineeEmail || '',
          traineeId: data.traineeId || data.createdBy || '',
          createdBy: data.createdByName || 'System',
          createdByName: data.createdByName || 'System',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          prospectId: data.prospectId || null,
          profileFlags: data.profileFlags || {
            age25Plus: false,
            married: false,
            dependentChildren: false,
            homeowner: false,
            solidCareer: false,
            income75kPlus: false,
            dissatisfied: false,
            entrepreneurial: false,
            spanishPreferred: false,
          },
          followUp: {
            appointmentHappened:
              data.followUp?.appointmentHappened ?? data.appointmentHappened ?? null,
            fnaTaken: data.followUp?.fnaTaken ?? data.fnaTaken ?? null,
            fnaWhyNot: data.followUp?.fnaWhyNot ?? data.fnaWhyNot ?? "",
            secondAppointmentScheduled:
              data.followUp?.secondAppointmentScheduled ??
              data.secondAppointmentScheduled ??
              null,
            referrals: data.followUp?.referrals ?? data.referrals ?? null,
            friendBecamePartner:
              data.followUp?.friendBecamePartner ?? data.friendBecamePartner ?? null,
            traineeEdified: data.followUp?.traineeEdified ?? data.traineeEdified ?? null,
            madeSale: data.followUp?.madeSale ?? data.madeSale ?? null,
            followUpNotes: data.followUp?.followUpNotes ?? data.followUpNotes ?? "",
            noShowReason: data.followUp?.noShowReason ?? data.noShowReason ?? "",
            scheduleAnother: data.followUp?.scheduleAnother ?? data.scheduleAnother ?? false,
            followUpCompleted:
              data.followUp?.followUpCompleted ?? data.followUpCompleted ?? false,
          }
        };
      });

      // Merge demo data with Firebase data
      const allRequests = [...DEMO_REQUESTS, ...requests]
        .sort((a, b) => {
          // Sort by date
          return a.requestDate - b.requestDate;
        });

      setMatchupRequests(allRequests);
    } catch (error) {
      console.error('Error refreshing matchup requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAgent = async (requestId, agentName, agentId) => {
    try {
      // Update in Firebase if it's a real request
      const request = matchupRequests.find(r => r.id === requestId);
      if (request && !request.id.startsWith('demo-')) {
        const appointmentRef = doc(db, 'appointments', requestId);
        await updateDoc(appointmentRef, {
          assignedAgent: agentName,
          assignedAgentId: agentId || null,
          matchUpStatus: 'pending',
          trainerApproved: false,
          assignedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Send email to trainer
        try {
          const trainer = agentOptions.find(agent => agent.id === agentId);
          const trainerEmail = trainer?.email;
          const currentUserName = localStorage.getItem('wb.name') || user.displayName;
          
          if (trainerEmail) {
            const appointmentDate = request.preferredDate instanceof Date ? 
              request.preferredDate : 
              new Date(request.preferredDate);
            const appointmentTime = request.preferredTime || 'TBD';
            
            await sendTrainerAssignmentEmail({
              trainerEmail: trainerEmail,
              trainerName: agentName,
              prospectName: request.clientName,
              appointmentType: request.appointmentType,
              appointmentDate: appointmentDate.toLocaleDateString ? appointmentDate.toLocaleDateString() : appointmentDate,
              appointmentTime: appointmentTime,
              meetingType: request.meetingType || 'Not specified',
              meetingUrl: request.meetingUrl || null,
              notes: request.notes || '',
              assignedBy: currentUserName
            });
            console.log('✅ Trainer assignment email sent successfully');
          }
        } catch (emailError) {
          console.error('Failed to send trainer assignment email:', emailError);
          // Don't fail the whole operation if email fails
        }
      }

      // Update local state
      setMatchupRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, assignedAgent: agentName, assignedAgentId: agentId || null, status: 'pending', trainerApproved: false }
            : req
        )
      );
    } catch (error) {
      console.error('Error assigning agent:', error);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const request = matchupRequests.find(r => r.id === requestId);
      if (request && !request.id.startsWith('demo-')) {
        const appointmentRef = doc(db, 'appointments', requestId);
        await updateDoc(appointmentRef, {
          trainerApproved: true,
          trainerApprovedAt: serverTimestamp(),
          matchUpStatus: 'matched',
          updatedAt: serverTimestamp()
        });
      }

      // Update local state
      setMatchupRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, trainerApproved: true, trainerApprovedAt: new Date(), status: 'matched' }
            : req
        )
      );

      alert('✅ Request approved successfully!');
      await onRefresh();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('❌ Failed to approve request');
    }
  };

  const handleDenyRequest = async (requestId) => {
    // if (!confirm('Are you sure you want to deny this training request? You will be removed as the trainer.')) {
    //   return;
    // }

    try {
      const request = matchupRequests.find(r => r.id === requestId);
      if (request && !request.id.startsWith('demo-')) {
        const appointmentRef = doc(db, 'appointments', requestId);
        await updateDoc(appointmentRef, {
          assignedAgent: null,
          assignedAgentId: null,
          trainerApproved: false,
          trainerApprovedAt: null,
          matchUpStatus: 'new',
          updatedAt: serverTimestamp()
        });
      }

      // Update local state
      setMatchupRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, assignedAgent: null, assignedAgentId: null, trainerApproved: false, trainerApprovedAt: null, status: 'new' }
            : req
        )
      );

      alert('✅ Request denied. Trainer removed.');
      await onRefresh();
    } catch (error) {
      console.error('Error denying request:', error);
      alert('❌ Failed to deny request');
    }
  };

  const handleFollowUp = (request) => {
    setFollowUpRequest(request);
    
    // Populate form with existing data from database if available
    const fu = request.followUp || {};
    setFollowUpForm({
      appointmentHappened: fu.appointmentHappened ?? request.appointmentHappened ?? null,
      fnaTaken: fu.fnaTaken ?? request.fnaTaken ?? null,
      fnaWhyNot: fu.fnaWhyNot ?? request.fnaWhyNot ?? '',
      secondAppointmentScheduled:
        fu.secondAppointmentScheduled ?? request.secondAppointmentScheduled ?? null,
      referrals: fu.referrals ?? request.referrals ?? null,
      friendBecamePartner:
        fu.friendBecamePartner ?? request.friendBecamePartner ?? null,
      traineeEdified: fu.traineeEdified ?? request.traineeEdified ?? null,
      madeSale: fu.madeSale ?? request.madeSale ?? null,
      notesYes:
        (fu.followUpNotes ?? request.followUpNotes) && (fu.appointmentHappened ?? request.appointmentHappened)
          ? (fu.followUpNotes ?? request.followUpNotes)
          : '',
      reason: fu.noShowReason ?? request.noShowReason ?? '',
      notesNo:
        (fu.followUpNotes ?? request.followUpNotes) && !(fu.appointmentHappened ?? request.appointmentHappened)
          ? (fu.followUpNotes ?? request.followUpNotes)
          : '',
      scheduleAnother: fu.scheduleAnother ?? request.scheduleAnother ?? false
    });
    setShowFollowUpModal(true);
  };

  const handleFollowUpSubmit = async () => {
    if (followUpForm.appointmentHappened === null) {
      alert('Please answer if the appointment happened');
      return;
    }

    try {
      setLoading(true);
      const currentUserName = localStorage.getItem('wb.name') || user?.displayName;

      if (followUpRequest && !followUpRequest.id.startsWith('demo-')) {
        const appointmentRef = doc(db, 'appointments', followUpRequest.id);
        
        const updateData = {
          updatedAt: serverTimestamp(),
          // Store follow-up only inside followUp object for consistency
          followUp: {
            appointmentHappened: followUpForm.appointmentHappened,
            fnaTaken: followUpForm.fnaTaken ?? null,
            fnaWhyNot: followUpForm.fnaWhyNot || '',
            secondAppointmentScheduled: followUpForm.secondAppointmentScheduled ?? null,
            referrals: followUpForm.referrals ?? null,
            friendBecamePartner: followUpForm.friendBecamePartner ?? null,
            traineeEdified: followUpForm.traineeEdified ?? null,
            madeSale: followUpForm.madeSale ?? null,
            followUpNotes: followUpForm.appointmentHappened ? (followUpForm.notesYes || '') : (followUpForm.notesNo || ''),
            noShowReason: followUpForm.appointmentHappened ? '' : (followUpForm.reason || ''),
            scheduleAnother: followUpForm.scheduleAnother ?? false,
            followUpCompleted: true,
            followUpCompletedAt: serverTimestamp(),
            followUpBy: currentUserName,
          },
        };

        if (followUpForm.appointmentHappened) {
          // Appointment happened - mark as completed
          updateData.completed = true;
          updateData.matchUpStatus = 'completed';
        } else {
          // Appointment did not happen
          // Add any additional status handling only
          if (!followUpForm.scheduleAnother) {
            updateData.matchUpStatus = 'cancelled';
          }
        }

        await updateDoc(appointmentRef, updateData);
      }

      // Update local state
      setMatchupRequests(prev => 
        prev.map(req => 
          req.id === followUpRequest.id 
            ? { 
                ...req, 
                followUp: {
                  ...(req.followUp || {}),
                  appointmentHappened: followUpForm.appointmentHappened,
                  fnaTaken: followUpForm.fnaTaken ?? null,
                  fnaWhyNot: followUpForm.fnaWhyNot || '',
                  secondAppointmentScheduled: followUpForm.secondAppointmentScheduled ?? null,
                  referrals: followUpForm.referrals ?? null,
                  friendBecamePartner: followUpForm.friendBecamePartner ?? null,
                  traineeEdified: followUpForm.traineeEdified ?? null,
                  madeSale: followUpForm.madeSale ?? null,
                  followUpNotes: followUpForm.appointmentHappened ? (followUpForm.notesYes || '') : (followUpForm.notesNo || ''),
                  noShowReason: followUpForm.appointmentHappened ? '' : (followUpForm.reason || ''),
                  scheduleAnother: followUpForm.scheduleAnother ?? false,
                  followUpCompleted: true,
                  followUpCompletedAt: new Date(),
                  followUpBy: currentUserName,
                },
                status: followUpForm.appointmentHappened ? 'completed' : (followUpForm.scheduleAnother ? req.status : 'cancelled')
              }
            : req
        )
      );

      setShowFollowUpModal(false);
      setFollowUpRequest(null);
      await onRefresh();
      alert('✅ Follow-up completed successfully!');
    } catch (error) {
      console.error('Error submitting follow-up:', error);
      alert('❌ Failed to submit follow-up');
    } finally {
      setLoading(false);
    }
  };


  const getFilteredRequests = () => {
    let filtered = matchupRequests;
    
    // Filter by status
    if (filter !== "all") {
      filtered = filtered.filter(request => request.status === filter);
    }
    
    // Filter by appointment type
    if (appointmentTypeFilter !== "all") {
      filtered = filtered.filter(request => request.appointmentType === appointmentTypeFilter);
    }
    
    return filtered;
  };

  const getSelectedDateRequests = () => {
    if (!selectedDate) return [];
    
    return getFilteredRequests().filter(request => {
      if (!request.preferredDate || request.preferredDate === 'TBD') return false;

      try {
        const requestDate = new Date(request.preferredDate);
        if (!isNaN(requestDate.getTime())) {
          return (
            requestDate.getMonth() === selectedDate.month &&
            requestDate.getFullYear() === selectedDate.year &&
            requestDate.getDate() === selectedDate.day
          );
        }
      } catch (e) {
        const preferredDate = request.preferredDate.toLowerCase();
        const monthName = getMonthName(selectedDate.month).toLowerCase();
        return preferredDate.includes(selectedDate.day.toString()) && preferredDate.includes(monthName);
      }

      return false;
    });
  };

  const getQuadrantColor = (quadrant) => {
    switch (quadrant) {
      case 'Urgent/Important': return '#ff6b6b';      // red
      case 'Not Urgent/Important': return '#4ecdc4';   // teal
      case 'Urgent/Not Important': return '#ffe66d';   // yellow
      case 'Not Urgent/Not Important': return '#a29bfe'; // purple
      default: return '#4ecdc4';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#888888';
      case 'pending': return '#ff8800';
      case 'matched': return '#00aa00';
      case 'completed': return '#0088ff';
      default: return '#888888';
    }
  };

  const handleClientNameChange = (e) => {
    const value = e.target.value;
    setNewMatchupForm({...newMatchupForm, clientName: value});
    setSelectedProspectId(null); // Clear selection when typing
    
    if (value.trim().length > 0) {
      const filtered = prospects.filter(p => 
        p.name && p.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProspects(filtered);
      setShowProspectDropdown(true);
    } else {
      setShowProspectDropdown(false);
    }
  };

  const handleSelectProspect = (prospect) => {
    setSelectedProspectId(prospect.id);
    setNewMatchupForm({
      ...newMatchupForm,
      clientName: prospect.name || '',
      prospectEmail: prospect.email || '',
      prospectPhone: prospect.phone || '',
      profileFlags: {
        age25Plus: prospect.profile?.flags?.age25Plus || false,
        married: prospect.profile?.flags?.married || false,
        dependentChildren: prospect.profile?.flags?.dependentChildren || false,
        homeowner: prospect.profile?.flags?.homeowner || false,
        solidCareer: prospect.profile?.flags?.solidCareer || false,
        income75kPlus: prospect.profile?.flags?.income75kPlus || false,
        dissatisfied: prospect.profile?.flags?.dissatisfied || false,
        entrepreneurial: prospect.profile?.flags?.entrepreneurial || false,
        spanishPreferred: prospect.profile?.flags?.spanishPreferred || false,
      }
    });
    setShowProspectDropdown(false);
  };

  const handleCreateMatchup = async () => {
    if (!user || !newMatchupForm.clientName) {
      alert('Please fill in required fields');
      return;
    }

    // Prevent double submissions
    if (loading) {
      console.warn('⚠️ Form submission already in progress');
      return;
    }

    try {
      setLoading(true);

      // If no prospect is selected, create a new prospect
      let prospectId = selectedProspectId;
      if (!prospectId && newMatchupForm.clientName) {
        try {
          const prospectsRef = collection(db, "prospects");
          
          // Check if prospect with same name and email already exists to prevent duplicates
          let existingProspect = null;
          if (newMatchupForm.prospectEmail) {
            const emailQuery = query(
              prospectsRef,
              where("email", "==", newMatchupForm.prospectEmail)
            );
            const emailSnapshot = await getDocs(emailQuery);
            if (emailSnapshot.docs.length > 0) {
              existingProspect = emailSnapshot.docs[0];
              console.log('⚠️ Prospect with this email already exists:', existingProspect.id);
            }
          }
          
          // If no prospect found by email, check by name
          if (!existingProspect && newMatchupForm.clientName) {
            const nameQuery = query(
              prospectsRef,
              where("name", "==", newMatchupForm.clientName)
            );
            const nameSnapshot = await getDocs(nameQuery);
            if (nameSnapshot.docs.length > 0) {
              existingProspect = nameSnapshot.docs[0];
              console.log('⚠️ Prospect with this name already exists:', existingProspect.id);
            }
          }
          
          if (existingProspect) {
            prospectId = existingProspect.id;
            console.log('✅ Using existing prospect:', prospectId);
          } else {
            const newProspectData = {
              name: newMatchupForm.clientName,
              email: newMatchupForm.prospectEmail || '',
              phone: newMatchupForm.prospectPhone || '',
              status: 'active',
              profile: {
                flags: newMatchupForm.profileFlags
              },
              createdAt: serverTimestamp(),
              createdBy: user?.email || localStorage.getItem('wb.email') || 'Unknown',
              recruiter: localStorage.getItem('wb.name') || user?.displayName || 'Unknown'
            };
            const prospectDocRef = await addDoc(prospectsRef, newProspectData);
            prospectId = prospectDocRef.id;
            console.log('✅ New prospect created:', prospectId);
          }
        } catch (prospectError) {
          console.error('Error creating prospect:', prospectError);
          // Continue even if prospect creation fails
        }
      }
      const currentUserName = localStorage.getItem('wb.name') || user.displayName;
      const currentUserId = localStorage.getItem('wb.userId') || user.uid;

      // Parse date and time
      const dateTimeStr = `${newMatchupForm.preferredDate}T${newMatchupForm.preferredTime || '12:00'}`;
      const appointmentDate = new Date(dateTimeStr);
      
      // Create appointment with matchUp flag
      const appointmentData = {
        title: `Matchup: ${newMatchupForm.clientName}`,
        type: newMatchupForm.appointmentType,
        quadrant: newMatchupForm.quadrant,
        matchUp: true,
        matchUpStatus: 'pending',
        prospectId: prospectId || null,
        prospectName: newMatchupForm.clientName,
        prospectEmail: newMatchupForm.prospectEmail || '',
        prospectPhone: newMatchupForm.prospectPhone || '',
        notes: newMatchupForm.notes || '',
        duration: parseInt(newMatchupForm.duration) || 60,
        dateTime: Timestamp.fromDate(appointmentDate),
        endTime: Timestamp.fromDate(new Date(appointmentDate.getTime() + (newMatchupForm.duration * 60 * 1000))),
        userId: newMatchupForm.traineeId || currentUserId,
        createdBy: newMatchupForm.traineeId || currentUserId,
        createdByName: newMatchupForm.traineeName || currentUserName,
        traineeId: newMatchupForm.traineeId || currentUserId,
        traineeName: newMatchupForm.traineeName || currentUserName,
        traineeEmail: newMatchupForm.traineeEmail || (user?.email || localStorage.getItem('wb.email')),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        completed: false,
        assignedAgent: newMatchupForm.assignedAgent || null,
        assignedAgentId: newMatchupForm.assignedAgentId || null,
        trainerApproved: false,
        matchUpStatus: newMatchupForm.assignedAgent ? 'pending' : 'new',
        timezone: newMatchupForm.timezone,
        appointmentType: newMatchupForm.appointmentType,
        meetingType: newMatchupForm.meetingType,
        meetingUrl: newMatchupForm.meetingUrl || null,
        meetingAddress: newMatchupForm.meetingAddress || null,
        meetingCity: newMatchupForm.meetingCity || null,
        meetingState: newMatchupForm.meetingState || null,
        meetingZip: newMatchupForm.meetingZip || null,
        profileFlags: newMatchupForm.profileFlags
      };

      await addDoc(collection(db, 'appointments'), appointmentData);
      
      // Send email to trainer if assigned
      if (newMatchupForm.assignedAgent && newMatchupForm.assignedAgentId) {
        try {
          // Get trainer's email from agents list
          const trainer = agentOptions.find(agent => agent.id === newMatchupForm.assignedAgentId);
          const trainerEmail = trainer?.email;
          
          if (trainerEmail) {
            await sendTrainerAssignmentEmail({
              trainerEmail: trainerEmail,
              trainerName: newMatchupForm.assignedAgent,
              prospectName: newMatchupForm.clientName,
              appointmentType: newMatchupForm.appointmentType,
              appointmentDate: appointmentDate.toLocaleDateString(),
              appointmentTime: appointmentDate.toLocaleTimeString(),
              meetingType: newMatchupForm.meetingType,
              meetingUrl: newMatchupForm.meetingUrl || null,
              notes: newMatchupForm.notes || '',
              assignedBy: currentUserName
            });
            console.log('✅ Trainer assignment email sent successfully');
          }
        } catch (emailError) {
          console.error('Failed to send trainer assignment email:', emailError);
          // Don't fail the whole operation if email fails
        }
      }
      
      // Reset form and close modal
      setNewMatchupForm({
        clientName: '',
        appointmentType: 'Consultation',
        timezone: 'America/New_York',
        preferredDate: '',
        preferredTime: '',
        duration: 60,
        quadrant: 'Not Urgent/Important',
        meetingType: 'URL',
        meetingUrl: '',
        meetingAddress: '',
        meetingCity: '',
        meetingState: '',
        meetingZip: '',
        notes: '',
        prospectEmail: '',
        prospectPhone: '',
        traineeName: localStorage.getItem('wb.name') || '',
        traineeEmail: localStorage.getItem('wb.email') || '',
        traineeId: localStorage.getItem('wb.userId') || '',
        assignedAgent: '',
        assignedAgentId: '',
        profileFlags: {
          age25Plus: false,
          married: false,
          dependentChildren: false,
          homeowner: false,
          solidCareer: false,
          income75kPlus: false,
          dissatisfied: false,
          entrepreneurial: false,
          spanishPreferred: false,
        }
      });
      setSelectedProspectId(null);
      setShowProspectDropdown(false);
      setFilteredProspects([]);
      setShowNewMatchupModal(false);
      
      // Refresh the list
      await onRefresh();
      
      alert('✅ Matchup request created successfully!');
    } catch (error) {
      console.error('Failed to create matchup:', error);
      alert(`❌ Failed to create matchup: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMatchup = (request) => {
    // Populate form with existing data
    console.log('🔍 Editing request:', request);
    const dateTime = request.dateTime || new Date();
    const preferredDate = dateTime instanceof Date ? dateTime.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const preferredTime = dateTime instanceof Date ? dateTime.toTimeString().substring(0, 5) : '12:00';

    const formData = {
      clientName: request.clientName || '',
      appointmentType: request.appointmentType || 'Consultation',
      timezone: request.timezone || 'America/New_York',
      preferredDate: preferredDate,
      preferredTime: preferredTime,
      duration: request.duration || 60,
      quadrant: request.quadrant || 'Not Urgent/Important',
      meetingType: request.meetingType || 'URL',
      meetingUrl: request.meetingUrl || '',
      meetingAddress: request.meetingAddress || '',
      meetingCity: request.meetingCity || '',
      meetingState: request.meetingState || '',
      meetingZip: request.meetingZip || '',
      notes: request.notes || '',
      prospectEmail: request.prospectEmail || request.contactInfo?.email || '',
      prospectPhone: request.prospectPhone || request.contactInfo?.phone || '',
      traineeName: request.traineeName || request.createdByName || '',
      traineeEmail: request.traineeEmail || '',
      traineeId: request.traineeId || request.createdBy || '',
      assignedAgent: request.assignedAgent || '',
      assignedAgentId: request.assignedAgentId || '',
      profileFlags: request.profileFlags || {
        age25Plus: false,
        married: false,
        dependentChildren: false,
        homeowner: false,
        solidCareer: false,
        income75kPlus: false,
        dissatisfied: false,
        entrepreneurial: false,
        spanishPreferred: false,
      }
    };
    
    console.log('📝 Setting form data:', formData);
    setNewMatchupForm(formData);
    setSelectedProspectId(request.prospectId || null);
    setEditingMatchup(request);
    setShowNewMatchupModal(true);
  };

  const handleUpdateMatchup = async () => {
    if (!user || !newMatchupForm.clientName || !editingMatchup) {
      alert('Please fill in required fields');
      return;
    }

    // Prevent double submissions
    if (loading) {
      console.warn('⚠️ Form submission already in progress');
      return;
    }

    try {
      setLoading(true);
      const currentUserName = localStorage.getItem('wb.name') || user?.displayName;

      // Parse date and time
      const dateTimeStr = `${newMatchupForm.preferredDate}T${newMatchupForm.preferredTime || '12:00'}`;
      const appointmentDate = new Date(dateTimeStr);
      
      // Update appointment data
      const updateData = {
        prospectName: newMatchupForm.clientName,
        type: newMatchupForm.appointmentType,
        quadrant: newMatchupForm.quadrant,
        prospectEmail: newMatchupForm.prospectEmail || '',
        prospectPhone: newMatchupForm.prospectPhone || '',
        notes: newMatchupForm.notes || '',
        duration: parseInt(newMatchupForm.duration) || 60,
        dateTime: Timestamp.fromDate(appointmentDate),
        endTime: Timestamp.fromDate(new Date(appointmentDate.getTime() + (newMatchupForm.duration * 60 * 1000))),
        updatedAt: serverTimestamp(),
        timezone: newMatchupForm.timezone,
        appointmentType: newMatchupForm.appointmentType,
        meetingType: newMatchupForm.meetingType,
        meetingUrl: newMatchupForm.meetingUrl || null,
        meetingAddress: newMatchupForm.meetingAddress || null,
        meetingCity: newMatchupForm.meetingCity || null,
        meetingState: newMatchupForm.meetingState || null,
        meetingZip: newMatchupForm.meetingZip || null,
        assignedAgent: newMatchupForm.assignedAgent || null,
        assignedAgentId: newMatchupForm.assignedAgentId || null,
        trainerApproved: false,
        matchUpStatus: newMatchupForm.assignedAgent ? 'pending' : 'new',
        profileFlags: newMatchupForm.profileFlags
      };

      // Don't update demo requests
      if (!editingMatchup.id.startsWith('demo-')) {
        const appointmentRef = doc(db, 'appointments', editingMatchup.id);
        await updateDoc(appointmentRef, updateData);
      }
      
      // Send email to trainer if assigned or changed
      const trainerChanged = editingMatchup.assignedAgentId !== newMatchupForm.assignedAgentId;
      if (newMatchupForm.assignedAgent && newMatchupForm.assignedAgentId && trainerChanged) {
        try {
          // Get trainer's email from agents list
          const trainer = agentOptions.find(agent => agent.id === newMatchupForm.assignedAgentId);
          const trainerEmail = trainer?.email;
          
          if (trainerEmail) {
            await sendTrainerAssignmentEmail({
              trainerEmail: trainerEmail,
              trainerName: newMatchupForm.assignedAgent,
              prospectName: newMatchupForm.clientName,
              appointmentType: newMatchupForm.appointmentType,
              appointmentDate: appointmentDate.toLocaleDateString(),
              appointmentTime: appointmentDate.toLocaleTimeString(),
              meetingType: newMatchupForm.meetingType,
              meetingUrl: newMatchupForm.meetingUrl || null,
              notes: newMatchupForm.notes || '',
              assignedBy: currentUserName
            });
            console.log('✅ Trainer assignment email sent successfully');
          }
        } catch (emailError) {
          console.error('Failed to send trainer assignment email:', emailError);
          // Don't fail the whole operation if email fails
        }
      }
      
      // Reset form and close modal
      setNewMatchupForm({
        clientName: '',
        appointmentType: 'Consultation',
        timezone: 'America/New_York',
        preferredDate: '',
        preferredTime: '',
        duration: 60,
        quadrant: 'Not Urgent/Important',
        meetingType: 'URL',
        meetingUrl: '',
        meetingAddress: '',
        meetingCity: '',
        meetingState: '',
        meetingZip: '',
        notes: '',
        prospectEmail: '',
        prospectPhone: '',
        assignedAgent: '',
        assignedAgentId: '',
        profileFlags: {
          age25Plus: false,
          married: false,
          dependentChildren: false,
          homeowner: false,
          solidCareer: false,
          income75kPlus: false,
          dissatisfied: false,
          entrepreneurial: false,
          spanishPreferred: false,
        }
      });
      setSelectedProspectId(null);
      setShowProspectDropdown(false);
      setFilteredProspects([]);
      setEditingMatchup(null);
      setShowNewMatchupModal(false);
      
      // Refresh the list
      await onRefresh();
      
      alert('✅ Matchup request updated successfully!');
    } catch (error) {
      console.error('Failed to update matchup:', error);
      alert(`❌ Failed to update matchup: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="matchup-page-layout">
      {loading && (
        <div className="matchup-loading">
          <div className="matchup-loading-spinner"></div>
          <span>Loading matchup requests...</span>
        </div>
      )}
      
      {/* Left Sidebar */}
      <div className="matchup-sidebar">
        <div className="matchup-sidebar-header">
          <h2 className="matchup-sidebar-title">Matchup</h2>
        </div>
        
        <div className="matchup-sidebar-content">
          <div className="matchup-month-section">
            <div className="matchup-month-nav">
              <button className="month-nav-btn" onClick={goToPreviousMonth}>‹</button>
              <span className="current-month">{getMonthName(currentMonth)} {currentYear}</span>
              <button className="month-nav-btn" onClick={goToNextMonth}>›</button>
            </div>
          </div>
          
          <div className="matchup-action-buttons">
            <button 
              className="matchup-action-btn urgent"
              onClick={() => setShowNewMatchupModal(true)}
            >
              + New Matchup Request
            </button>
            {/* <button className="matchup-action-btn">
              + Assign Agent
            </button>
            <button className="matchup-action-btn">
              Setup Auto-Match
            </button> */}
          </div>
          
          {/* <div className="matchup-view-toggle">
            <span className="view-label">List View</span>
          </div> */}
          
          <div className="matchup-stats-section">
            <div className="matchup-stats-header">
              <h3>Matchup Stats</h3>
            </div>
            <div className="matchup-stats-cards">
              <div className="matchup-stat-card">
                <div className="stat-indicator" style={{backgroundColor: '#888888'}}></div>
                <div className="stat-content">
                  <div className="stat-title">New</div>
                  <div className="stat-count">{matchupRequests.filter(r => r.status === 'new').length}</div>
                </div>
              </div>
              <div className="matchup-stat-card">
                <div className="stat-indicator urgent"></div>
                <div className="stat-content">
                  <div className="stat-title">Pending</div>
                  <div className="stat-count">{matchupRequests.filter(r => r.status === 'pending').length}</div>
                </div>
              </div>
              <div className="matchup-stat-card">
                <div className="stat-indicator matched"></div>
                <div className="stat-content">
                  <div className="stat-title">Matched</div>
                  <div className="stat-count">{matchupRequests.filter(r => r.status === 'matched').length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="matchup-filter-section">
            <h3>Filter Requests</h3>
            <div className="matchup-filter-dropdowns" style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
              {/* Status Filter */}
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', opacity: 0.8 }}>Status</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All ({matchupRequests.length})</option>
                  <option value="new">🆕 New ({matchupRequests.filter(r => r.status === 'new').length})</option>
                  <option value="pending">⏳ Pending Approval ({matchupRequests.filter(r => r.status === 'pending').length})</option>
                  <option value="matched">✅ Matched ({matchupRequests.filter(r => r.status === 'matched').length})</option>
                  <option value="completed">✔️ Completed ({matchupRequests.filter(r => r.status === 'completed').length})</option>
                </select>
              </div>
              
              {/* Appointment Type Filter */}
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', opacity: 0.8 }}>Appointment Type</label>
                <select
                  value={appointmentTypeFilter}
                  onChange={(e) => setAppointmentTypeFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Types ({matchupRequests.length})</option>
                  <option value="Step 1&2">Step 1&2 ({matchupRequests.filter(r => r.appointmentType === 'Step 1&2').length})</option>
                  <option value="Step 1">Step 1 ({matchupRequests.filter(r => r.appointmentType === 'Step 1').length})</option>
                  <option value="Step 2">Step 2 ({matchupRequests.filter(r => r.appointmentType === 'Step 2').length})</option>
                  <option value="Step 3">Step 3 ({matchupRequests.filter(r => r.appointmentType === 'Step 3').length})</option>
                  <option value="Step 4">Step 4 ({matchupRequests.filter(r => r.appointmentType === 'Step 4').length})</option>
                  <option value="Step 5">Step 5 ({matchupRequests.filter(r => r.appointmentType === 'Step 5').length})</option>
                  <option value="P">P ({matchupRequests.filter(r => r.appointmentType === 'P').length})</option>
                  <option value="Other">Other ({matchupRequests.filter(r => r.appointmentType === 'Other').length})</option>
                </select>
              </div>
            </div>
          </div>

          {/* Selected Request Details */}
          {selectedRequest && (
            <div className="matchup-selected-request">
              <h3>Selected Request</h3>
              {(() => {
                const request = matchupRequests.find(r => r.id === selectedRequest);
                if (!request) return null;
                const selectedAgentValue =
                  agentOptions.find((opt) => opt.id === request.assignedAgentId)?.label ||
                  request.assignedAgent ||
                  "";
                return (
                  <div className="selected-request-card">
                    <div className="selected-request-header">
                      <h4>{request.clientName}</h4>
                      <div className="selected-request-badges">
                        <span className="priority-badge" style={{ backgroundColor: getQuadrantColor(request.quadrant) }}>
                          {request.quadrant}
                        </span>
                        <span className="status-badge" style={{ backgroundColor: getStatusColor(request.status) }}>
                          {request.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="selected-request-details">
                      <p><strong>Type:</strong> {request.appointmentType}</p>
                      <p><strong>Date:</strong> {request.preferredDate}</p>
                      <p><strong>Time:</strong> {request.preferredTime}</p>
                      <p><strong>Duration:</strong> {request.duration} minutes</p>
                      <p><strong>Trainee:</strong> {request.createdByName}</p>
                      {request.assignedAgent && (
                        <p><strong>Trainer:</strong> {request.assignedAgent}</p>
                      )}
                    </div>
                    <div className="selected-request-actions">
                      {request.status === 'new' && (
                        <div className="assign-agent-section">
                          <label>Assign Agent:</label>
                          <div
                            className="agent-picker"
                            onClick={() => {
                              setAgentSearchRequestId(request.id);
                              setAgentSearchOpen(true);
                            }}
                          >
                            <span>{selectedAgentValue || "Select..."}</span>
                            <button className="btn small">Assign</button>
                          </div>
                        </div>
                      )}
                      
                      {/* Trainer Approval Section - Only visible to assigned trainer */}
                      {request.status === 'pending' && request.assignedAgentId === user?.uid && (
                        <div className="trainer-approval-section">
                          <p style={{ color: '#d4af37', marginBottom: '10px', fontSize: '14px' }}>
                            ⚠️ You are assigned as trainer. Please approve or deny this request.
                          </p>
                          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                            <button 
                              className="action-btn success" 
                              onClick={() => handleApproveRequest(request.id)}
                              style={{ flex: 1 }}
                            >
                              ✅ Approve
                            </button>
                            <button 
                              className="action-btn" 
                              onClick={() => handleDenyRequest(request.id)}
                              style={{ flex: 1, backgroundColor: '#cc0000' }}
                            >
                              ❌ Deny
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="action-buttons">
                        <button className="action-btn primary" onClick={() => handleEditMatchup(request)}>✏️ Edit</button>
                        {(request.status === 'matched' || request.status === 'completed') && (
                          <button 
                            className="action-btn" 
                            onClick={() => handleFollowUp(request)}
                            style={{ backgroundColor: '#6366f1' }}
                          >
                            📋 Follow Up
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="matchup-main-content">
        <div className="matchup-content-header">
          <div className="matchup-content-title">
            <h1>Matchup Requests</h1>
            <p>Match appointments with available agents</p>
          </div>
          
          <div className="matchup-content-actions">
            <button
              className="matchup-btn matchup-btn-refresh"
              onClick={onRefresh}
              title="Refresh requests"
              disabled={loading}
            >
              <span className="btn-icon">🔄</span>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="matchup-calendar-container">
          <div className="calendar-grid">
            {/* Calendar Header */}
            <div className="calendar-header">
              {dayLabels.map((label) => (
                <div key={label} className="day-header">{label}</div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="calendar-days">
              {(() => {
                const firstDayRaw = getFirstDayOfMonth(currentMonth, currentYear);
                const daysInMonthRaw = getDaysInMonth(currentMonth, currentYear);

                // Guard against any unexpected NaN values so the grid doesn't misalign
                const firstDay = Number.isFinite(firstDayRaw) ? firstDayRaw : 0;
                const daysInMonth = Number.isFinite(daysInMonthRaw) ? daysInMonthRaw : 31;

                const totalCells = firstDay + daysInMonth;
                const trailingEmpty = (7 - (totalCells % 7)) % 7; // fill to complete the final week
                const cellCount = totalCells + trailingEmpty;

                return Array.from({ length: cellCount }, (_, index) => {
                  const dayNumber = index - firstDay + 1;
                  const isCurrentMonthDay = dayNumber >= 1 && dayNumber <= daysInMonth;

                  if (!isCurrentMonthDay) {
                    return (
                      <div key={`empty-${index}`} className="calendar-day">
                        <div className="day-number"></div>
                      </div>
                    );
                  }

                  const dayRequests = getFilteredRequests().filter(request => {
                    if (!request.preferredDate || request.preferredDate === 'TBD') return false;

                    try {
                      const requestDate = new Date(request.preferredDate);
                      if (!isNaN(requestDate.getTime())) {
                        return (
                          requestDate.getMonth() === currentMonth &&
                          requestDate.getFullYear() === currentYear &&
                          requestDate.getDate() === dayNumber
                        );
                      }
                    } catch (e) {
                      const preferredDate = request.preferredDate.toLowerCase();
                      const monthName = getMonthName(currentMonth).toLowerCase();
                      return preferredDate.includes(dayNumber.toString()) && preferredDate.includes(monthName);
                    }

                    return false;
                  });

                  const isSelected = selectedDate && 
                    selectedDate.day === dayNumber && 
                    selectedDate.month === currentMonth && 
                    selectedDate.year === currentYear;

                  return (
                    <div
                      key={`day-${dayNumber}`}
                      className={`calendar-day ${dayRequests.length > 0 ? 'has-requests' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedDate({ day: dayNumber, month: currentMonth, year: currentYear });
                        if (dayRequests.length > 0) {
                          setSelectedRequest(dayRequests[0].id);
                        } else {
                          setSelectedRequest(null);
                        }
                      }}
                    >
                      <div className="day-number">
                        {dayNumber}
                        {dayRequests.length > 0 && (
                          <span style={{ 
                            marginLeft: '50px', 
                            fontSize: '0.50em', 
                            backgroundColor: 'rgba(59, 130, 246, 0.8)',
                            padding: '2px 6px',
                            borderRadius: '50%',
                            fontWeight: '600'
                          }}>
                            {dayRequests.length}
                          </span>
                        )}
                      </div>
                      {dayRequests.length > 0 && (
                        <div className="day-requests">
                          {dayRequests.slice(0, 3).map((request) => (
                            <div
                              key={request.id}
                              className={`day-request ${request.quadrant}`}
                              style={{ backgroundColor: getQuadrantColor(request.quadrant) }}
                              title={`${request.clientName} - ${request.appointmentType}`}
                            >
                              {request.clientName.split(' ')[0]}
                            </div>
                          ))}
                          {dayRequests.length > 3 && <div className="day-request more">+{dayRequests.length - 3}</div>}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>

        {/* Selected Date Requests List */}
        <div className="matchup-selected-requests">
          <h3>Requests for Selected Date</h3>
          {!selectedDate ? (
            <div className="matchup-empty-state">
              <div className="empty-state-icon">📅</div>
              <h3>No date selected</h3>
              <p>Click on a date in the calendar to view requests</p>
            </div>
          ) : getSelectedDateRequests().length === 0 ? (
            <div className="matchup-empty-state">
              <div className="empty-state-icon">📅</div>
              <h3>No requests for {getMonthName(selectedDate.month)} {selectedDate.day}, {selectedDate.year}</h3>
              <p>Select another date or create a new matchup request</p>
            </div>
          ) : (
            <div className="requests-list">
              {getSelectedDateRequests().map((request) => (
                <div
                  key={request.id}
                  className={`request-item ${request.priority} ${selectedRequest === request.id ? 'selected' : ''}`}
                  onClick={() => setSelectedRequest(request.id)}
                >
                  <div className="request-time">{request.preferredTime}</div>
                  <div className="request-info">
                    <div className="request-client">{request.clientName}</div>
                    <div className="request-type">{request.appointmentType}</div>
                  </div>
                  <div className="request-status">
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(request.status) }}>
                      {request.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Agent Search Modal */}
      <AgentSearchModal
        open={agentSearchOpen}
        onClose={() => {
          setAgentSearchOpen(false);
          setAgentSearchRequestId(null);
        }}
        onSelect={(agentLabel, agentId) => {
          if (agentSearchRequestId === 'form') {
            // Update form state
            setNewMatchupForm(prev => ({
              ...prev,
              assignedAgent: agentLabel,
              assignedAgentId: agentId
            }));
          } else if (agentSearchRequestId) {
            // Update existing request
            handleAssignAgent(agentSearchRequestId, agentLabel, agentId);
          }
          setAgentSearchOpen(false);
          setAgentSearchRequestId(null);
        }}
        items={agentOptions}
      />

      {/* New/Edit Matchup Request Modal */}
      {showNewMatchupModal && (
        <div className="matchup-modal-overlay" onClick={() => {
          setShowNewMatchupModal(false);
          setEditingMatchup(null);
          setSelectedProspectId(null);
          setShowProspectDropdown(false);
          setShowTraineeDropdown(false);
        }}>
          <div className="matchup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="matchup-modal-header">
              <h3>{editingMatchup ? 'Edit Matchup Request' : 'Create New Matchup Request'}</h3>
              <button onClick={() => {
                setShowNewMatchupModal(false);
                setEditingMatchup(null);
                setSelectedProspectId(null);
                setShowProspectDropdown(false);
                setShowTraineeDropdown(false);
              }}>✕</button>
            </div>
            <div className="matchup-modal-body">
              {/* Timezone */}
              <div className="form-group">
                <label>Timezone *</label>
                <select 
                  value={newMatchupForm.timezone}
                  onChange={(e) => setNewMatchupForm({...newMatchupForm, timezone: e.target.value})}
                >
                  <optgroup label="North America">
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Phoenix">Mountain Time - Arizona (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Anchorage">Alaska Time (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii-Aleutian Time (HST)</option>
                    <option value="America/Toronto">Eastern Time - Toronto</option>
                    <option value="America/Vancouver">Pacific Time - Vancouver</option>
                    <option value="America/Mexico_City">Central Time - Mexico City</option>
                    <option value="America/Cancun">Eastern Time - Cancun</option>
                  </optgroup>
                  <optgroup label="South America">
                    <option value="America/Sao_Paulo">Brasilia Time (BRT)</option>
                    <option value="America/Argentina/Buenos_Aires">Argentina Time (ART)</option>
                    <option value="America/Santiago">Chile Time (CLT)</option>
                    <option value="America/Lima">Peru Time (PET)</option>
                    <option value="America/Bogota">Colombia Time (COT)</option>
                    <option value="America/Caracas">Venezuela Time (VET)</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="UTC">UTC</option>
                    <option value="Europe/London">Greenwich Mean Time (GMT)</option>
                    <option value="Europe/Dublin">Irish Standard Time (IST)</option>
                    <option value="Europe/Paris">Central European Time (CET)</option>
                    <option value="Europe/Berlin">Central European Time - Berlin</option>
                    <option value="Europe/Madrid">Central European Time - Madrid</option>
                    <option value="Europe/Rome">Central European Time - Rome</option>
                    <option value="Europe/Amsterdam">Central European Time - Amsterdam</option>
                    <option value="Europe/Brussels">Central European Time - Brussels</option>
                    <option value="Europe/Zurich">Central European Time - Zurich</option>
                    <option value="Europe/Vienna">Central European Time - Vienna</option>
                    <option value="Europe/Athens">Eastern European Time (EET)</option>
                    <option value="Europe/Helsinki">Eastern European Time - Helsinki</option>
                    <option value="Europe/Istanbul">Turkey Time (TRT)</option>
                    <option value="Europe/Moscow">Moscow Standard Time (MSK)</option>
                  </optgroup>
                  <optgroup label="Africa">
                    <option value="Africa/Cairo">Eastern European Time - Cairo</option>
                    <option value="Africa/Johannesburg">South Africa Standard Time (SAST)</option>
                    <option value="Africa/Lagos">West Africa Time (WAT)</option>
                    <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                  </optgroup>
                  <optgroup label="Middle East">
                    <option value="Asia/Dubai">Gulf Standard Time (GST)</option>
                    <option value="Asia/Jerusalem">Israel Standard Time (IST)</option>
                    <option value="Asia/Riyadh">Arabia Standard Time (AST)</option>
                    <option value="Asia/Tehran">Iran Standard Time (IRST)</option>
                  </optgroup>
                  <optgroup label="Asia">
                    <option value="Asia/Kolkata">India Standard Time (IST)</option>
                    <option value="Asia/Karachi">Pakistan Standard Time (PKT)</option>
                    <option value="Asia/Dhaka">Bangladesh Standard Time (BST)</option>
                    <option value="Asia/Bangkok">Indochina Time (ICT)</option>
                    <option value="Asia/Singapore">Singapore Standard Time (SGT)</option>
                    <option value="Asia/Hong_Kong">Hong Kong Time (HKT)</option>
                    <option value="Asia/Shanghai">China Standard Time (CST)</option>
                    <option value="Asia/Taipei">Taipei Standard Time (TST)</option>
                    <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
                    <option value="Asia/Seoul">Korea Standard Time (KST)</option>
                    <option value="Asia/Manila">Philippine Time (PHT)</option>
                    <option value="Asia/Jakarta">Western Indonesian Time (WIB)</option>
                  </optgroup>
                  <optgroup label="Australia & Pacific">
                    <option value="Australia/Sydney">Australian Eastern Time (AEST)</option>
                    <option value="Australia/Melbourne">Australian Eastern Time - Melbourne</option>
                    <option value="Australia/Brisbane">Australian Eastern Time - Brisbane</option>
                    <option value="Australia/Adelaide">Australian Central Time (ACST)</option>
                    <option value="Australia/Perth">Australian Western Time (AWST)</option>
                    <option value="Pacific/Auckland">New Zealand Time (NZST)</option>
                    <option value="Pacific/Fiji">Fiji Time (FJT)</option>
                    <option value="Pacific/Guam">Chamorro Standard Time (ChST)</option>
                  </optgroup>
                </select>
              </div>

              {/* Client Name - Autocomplete */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Client Name *</label>
                <input 
                  type="text" 
                  value={newMatchupForm.clientName}
                  onChange={handleClientNameChange}
                  onFocus={() => {
                    if (newMatchupForm.clientName && filteredProspects.length > 0) {
                      setShowProspectDropdown(true);
                    }
                  }}
                  onBlur={() => {
                    // Delay to allow click on dropdown item
                    setTimeout(() => setShowProspectDropdown(false), 200);
                  }}
                  placeholder="Type to search prospects or enter new name"
                  autoComplete="off"
                />
                {showProspectDropdown && filteredProspects.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: '#1f2937',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    marginTop: '4px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}>
                    {filteredProspects.map(prospect => (
                      <div
                        key={prospect.id}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur from firing
                          handleSelectProspect(prospect);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          color: '#e5e7eb',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ fontWeight: '600' }}>{prospect.name}</div>
                        {prospect.email && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                            {prospect.email}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Appointment Type */}
              <div className="form-group">
                <label>Appointment Type</label>
                <select 
                  value={newMatchupForm.appointmentType}
                  onChange={(e) => setNewMatchupForm({...newMatchupForm, appointmentType: e.target.value})}
                >
                  <option value="Step 1&2">Step 1&2</option>
                  <option value="Step 1">Step 1</option>
                  <option value="Step 2">Step 2</option>
                  <option value="Step 3">Step 3</option>
                  <option value="Step 4">Step 4</option>
                  <option value="Step 5">Step 5</option>
                  <option value="P">P</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Preferred Date and Time */}
              <div className="form-row">
                <div className="form-group">
                  <label>Preferred Date</label>
                  <input 
                    type="date" 
                    value={newMatchupForm.preferredDate}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, preferredDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Preferred Time</label>
                  <input 
                    type="time" 
                    value={newMatchupForm.preferredTime}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, preferredTime: e.target.value})}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="form-group">
                <label className="section-header">Contact Information</label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={newMatchupForm.prospectEmail}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, prospectEmail: e.target.value})}
                    placeholder="client@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input 
                    type="tel" 
                    value={newMatchupForm.prospectPhone}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, prospectPhone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
{/* Trainee Section */}
              <div className="form-group">
                <label className="section-header">Trainee</label>
              </div>
              
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Select Trainee *</label>
                <input
                  type="text"
                  value={newMatchupForm.traineeName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewMatchupForm({...newMatchupForm, traineeName: value});
                    
                    if (value.trim().length > 0) {
                      const filtered = agentOptions.filter(agent => 
                        agent.label.toLowerCase().includes(value.toLowerCase()) ||
                        (agent.email && agent.email.toLowerCase().includes(value.toLowerCase()))
                      );
                      setFilteredTrainees(filtered);
                      setShowTraineeDropdown(true);
                    } else {
                      setShowTraineeDropdown(false);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowTraineeDropdown(false), 200)}
                  placeholder="Type to search trainee..."
                />
                {showTraineeDropdown && filteredTrainees.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {filteredTrainees.map(trainee => (
                      <div
                        key={trainee.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setNewMatchupForm({
                            ...newMatchupForm,
                            traineeName: trainee.name || trainee.label,
                            traineeEmail: trainee.email || '',
                            traineeId: trainee.id
                          });
                          setShowTraineeDropdown(false);
                        }}
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          ':hover': { background: 'rgba(255,255,255,0.1)' }
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        <div style={{ fontWeight: '500' }}>{trainee.name || trainee.label}</div>
                        {trainee.email && (
                          <div style={{ fontSize: '0.85em', opacity: 0.7 }}>{trainee.email}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Trainee Email</label>
                <input
                  type="email"
                  value={newMatchupForm.traineeEmail}
                  disabled
                  style={{ opacity: 0.7, cursor: 'not-allowed' }}
                  placeholder="Selected trainee's email"
                />
              </div>

              {/* Trainer/Agent Section */}
              <div className="form-group">
                <label className="section-header">Trainer</label>
              </div>
              
              <div className="form-group">
                <label>Assign Trainer/Agent</label>
                <div
                  className="agent-picker"
                  style={{ cursor: 'pointer', padding: '10px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', background: 'rgba(255,255,255,0.06)' }}
                  onClick={() => {
                    setAgentSearchRequestId('form');
                    setAgentSearchOpen(true);
                  }}
                >
                  <span>{newMatchupForm.assignedAgent || "Select Trainer..."}</span>
                  <button className="btn small" style={{ marginLeft: '10px' }}>
                    {newMatchupForm.assignedAgent ? 'Change' : 'Select'}
                  </button>
                </div>
              </div>

              {/* Profile Flags */}
              <div className="form-group">
                <label className="section-header">Profile</label>
                <div className="profile-flags-grid">
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.age25Plus}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, age25Plus: e.target.checked}
                      })}
                    />
                    <span>25+ yrs Old</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.married}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, married: e.target.checked}
                      })}
                    />
                    <span>Married</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.dependentChildren}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, dependentChildren: e.target.checked}
                      })}
                    />
                    <span>Dependent Children</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.homeowner}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, homeowner: e.target.checked}
                      })}
                    />
                    <span>Homeowner</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.solidCareer}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, solidCareer: e.target.checked}
                      })}
                    />
                    <span>Solid Career Background</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.income75kPlus}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, income75kPlus: e.target.checked}
                      })}
                    />
                    <span>$75k+ Income</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.dissatisfied}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, dissatisfied: e.target.checked}
                      })}
                    />
                    <span>Dissatisfied</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.entrepreneurial}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, entrepreneurial: e.target.checked}
                      })}
                    />
                    <span>Entrepreneurial</span>
                  </label>
                  <label className="flag-checkbox">
                    <input 
                      type="checkbox" 
                      checked={newMatchupForm.profileFlags.spanishPreferred}
                      onChange={(e) => setNewMatchupForm({
                        ...newMatchupForm, 
                        profileFlags: {...newMatchupForm.profileFlags, spanishPreferred: e.target.checked}
                      })}
                    />
                    <span>Spanish Speaking Preferred</span>
                  </label>
                </div>
              </div>

              {/* Duration and Meeting Type */}
              <div className="form-row">
                <div className="form-group">
                  <label>Duration (minutes)</label>
                  <input 
                    type="number" 
                    value={newMatchupForm.duration}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, duration: e.target.value})}
                    min="15"
                    step="15"
                  />
                </div>
                <div className="form-group">
                  <label>Meeting Type *</label>
                  <select 
                    value={newMatchupForm.meetingType}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, meetingType: e.target.value})}
                  >
                    <option value="URL">URL</option>
                    <option value="Address">Address</option>
                  </select>
                </div>
              </div>

              {/* Meeting Location - URL */}
              {newMatchupForm.meetingType === 'URL' && (
                <div className="form-group">
                  <label>Meeting URL *</label>
                  <input 
                    type="url" 
                    value={newMatchupForm.meetingUrl}
                    onChange={(e) => setNewMatchupForm({...newMatchupForm, meetingUrl: e.target.value})}
                    placeholder="https://example.com/meeting"
                  />
                </div>
              )}

              {/* Meeting Location - Address */}
              {newMatchupForm.meetingType === 'Address' && (
                <>
                  <div className="form-group">
                    <label>Address *</label>
                    <input 
                      type="text" 
                      value={newMatchupForm.meetingAddress}
                      onChange={(e) => setNewMatchupForm({...newMatchupForm, meetingAddress: e.target.value})}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input 
                        type="text" 
                        value={newMatchupForm.meetingCity}
                        onChange={(e) => setNewMatchupForm({...newMatchupForm, meetingCity: e.target.value})}
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <input 
                        type="text" 
                        value={newMatchupForm.meetingState}
                        onChange={(e) => setNewMatchupForm({...newMatchupForm, meetingState: e.target.value})}
                        placeholder="State"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Zip *</label>
                    <input 
                      type="text" 
                      value={newMatchupForm.meetingZip}
                      onChange={(e) => setNewMatchupForm({...newMatchupForm, meetingZip: e.target.value})}
                      placeholder="Zip code"
                    />
                  </div>
                </>
              )}

              {/* Quadrant */}
              <div className="form-group">
                <label>Choose Quadrant</label>
                <select 
                  value={newMatchupForm.quadrant}
                  onChange={(e) => setNewMatchupForm({...newMatchupForm, quadrant: e.target.value})}
                >
                  <option value="Urgent/Important">Urgent/Important</option>
                  <option value="Not Urgent/Important">Not Urgent/Important</option>
                  <option value="Urgent/Not Important">Urgent/Not Important</option>
                  <option value="Not Urgent/Not Important">Not Urgent/Not Important</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notes *</label>
                <textarea 
                  value={newMatchupForm.notes}
                  onChange={(e) => setNewMatchupForm({...newMatchupForm, notes: e.target.value})}
                  placeholder="Additional information about the request..."
                  rows="3"
                  required
                />
              </div>
            </div>
            <div className="matchup-modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowNewMatchupModal(false);
                  setEditingMatchup(null);
                  setSelectedProspectId(null);
                  setShowProspectDropdown(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={editingMatchup ? handleUpdateMatchup : handleCreateMatchup}
                disabled={loading || !newMatchupForm.clientName}
              >
                {loading ? (editingMatchup ? 'Updating...' : 'Creating...') : (editingMatchup ? 'Update Request' : 'Create Request')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow Up Modal */}
      {showFollowUpModal && followUpRequest && (
        <div className="matchup-modal-overlay" onClick={() => setShowFollowUpModal(false)}>
          <div className="matchup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="matchup-modal-header">
              <h3>Follow Up - {followUpRequest.clientName}</h3>
              <button onClick={() => setShowFollowUpModal(false)}>✕</button>
            </div>
            <div className="matchup-modal-body">
              {/* Initial Question */}
              <div className="form-group">
                <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>Did the appointment happen?</label>
                <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      name="appointmentHappened"
                      checked={followUpForm.appointmentHappened === true}
                      onChange={() => setFollowUpForm({ ...followUpForm, appointmentHappened: true })}
                      style={{ width: 'auto', margin: 0 }}
                    />
                    <span>Yes</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      name="appointmentHappened"
                      checked={followUpForm.appointmentHappened === false}
                      onChange={() => setFollowUpForm({ ...followUpForm, appointmentHappened: false })}
                      style={{ width: 'auto', margin: 0 }}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {/* Fields for YES */}
              {followUpForm.appointmentHappened === true && (
                <>
                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>Financial Needs Analysis (FNA) Taken?</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="fnaTaken"
                          checked={followUpForm.fnaTaken === true}
                          onChange={() => setFollowUpForm({ ...followUpForm, fnaTaken: true, fnaWhyNot: '' })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="fnaTaken"
                          checked={followUpForm.fnaTaken === false}
                          onChange={() => setFollowUpForm({ ...followUpForm, fnaTaken: false })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>No</span>
                      </label>
                    </div>
                    {followUpForm.fnaTaken === false && (
                      <div style={{ marginTop: '15px' }}>
                        <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: '#e5e7eb' }}>Why Not?</label>
                        <input 
                          type="text"
                          value={followUpForm.fnaWhyNot}
                          onChange={(e) => setFollowUpForm({ ...followUpForm, fnaWhyNot: e.target.value })}
                          placeholder="Please explain why FNA was not taken..."
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#e5e7eb' }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>2nd Appointment scheduled?</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="secondAppointment"
                          checked={followUpForm.secondAppointmentScheduled === true}
                          onChange={() => setFollowUpForm({ ...followUpForm, secondAppointmentScheduled: true })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="secondAppointment"
                          checked={followUpForm.secondAppointmentScheduled === false}
                          onChange={() => setFollowUpForm({ ...followUpForm, secondAppointmentScheduled: false })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>No</span>
                      </label>
                    </div>
                    {/* {followUpForm.secondAppointmentScheduled === true && (
                      <div style={{ marginTop: '15px' }}>
                        <span style={{ fontSize: '14px', color: '#e5e7eb' }}>Would you like to </span>
                        <span 
                          onClick={() => {
                            // Pre-fill the new matchup form with current request data
                            setNewMatchupForm({
                              clientName: followUpRequest.clientName || '',
                              appointmentType: 'Follow-up',
                              timezone: followUpRequest.timezone || 'America/New_York',
                              preferredDate: '',
                              preferredTime: '',
                              duration: 60,
                              priority: 'normal',
                              meetingType: followUpRequest.meetingType || 'Zoom',
                              meetingUrl: followUpRequest.meetingUrl || '',
                              meetingAddress: followUpRequest.meetingAddress || '',
                              meetingCity: followUpRequest.meetingCity || '',
                              meetingState: followUpRequest.meetingState || '',
                              meetingZip: followUpRequest.meetingZip || '',
                              notes: 'Follow-up appointment from previous training',
                              prospectEmail: followUpRequest.contactInfo?.email || '',
                              prospectPhone: followUpRequest.contactInfo?.phone || '',
                              assignedAgent: followUpRequest.assignedAgent || '',
                              assignedAgentId: followUpRequest.assignedAgentId || '',
                              profileFlags: followUpRequest.profileFlags || {
                                age25Plus: false,
                                homeowner: false,
                                solidCareer: false,
                                income75kPlus: false,
                                dissatisfied: false,
                                entrepreneurial: false,
                                spanishPreferred: false,
                              }
                            });
                            setShowNewMatchupModal(true);
                          }}
                          style={{ 
                            fontSize: '14px', 
                            color: '#3b82f6', 
                            textDecoration: 'underline', 
                            cursor: 'pointer' 
                          }}
                        >
                          create a follow-up appointment
                        </span>
                        <span style={{ fontSize: '14px', color: '#e5e7eb' }}>?</span>
                      </div>
                    )} */}
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>Referrals?</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="referrals"
                          checked={followUpForm.referrals === true}
                          onChange={() => setFollowUpForm({ ...followUpForm, referrals: true })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="referrals"
                          checked={followUpForm.referrals === false}
                          onChange={() => setFollowUpForm({ ...followUpForm, referrals: false })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>Did friend become a business partner?</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="friendPartner"
                          checked={followUpForm.friendBecamePartner === true}
                          onChange={() => setFollowUpForm({ ...followUpForm, friendBecamePartner: true })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="friendPartner"
                          checked={followUpForm.friendBecamePartner === false}
                          onChange={() => setFollowUpForm({ ...followUpForm, friendBecamePartner: false })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>Did trainee edify the trainer properly?</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="traineeEdified"
                          checked={followUpForm.traineeEdified === 'yes'}
                          onChange={() => setFollowUpForm({ ...followUpForm, traineeEdified: 'yes' })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="traineeEdified"
                          checked={followUpForm.traineeEdified === 'no'}
                          onChange={() => setFollowUpForm({ ...followUpForm, traineeEdified: 'no' })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>No</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="traineeEdified"
                          checked={followUpForm.traineeEdified === 'kinda'}
                          onChange={() => setFollowUpForm({ ...followUpForm, traineeEdified: 'kinda' })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Kinda</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', textTransform: 'unset' , marginBottom: '10px', display: 'block', color: '#d97706' }}>Did you make a sale?</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="madeSale"
                          checked={followUpForm.madeSale === true}
                          onChange={() => setFollowUpForm({ ...followUpForm, madeSale: true })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="madeSale"
                          checked={followUpForm.madeSale === false}
                          onChange={() => setFollowUpForm({ ...followUpForm, madeSale: false })}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', marginBottom: '10px', display: 'block', color: '#d97706' }}>Notes</label>
                    <textarea 
                      value={followUpForm.notesYes}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, notesYes: e.target.value })}
                      placeholder="Additional notes..."
                      rows="4"
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#e5e7eb' }}
                    />
                  </div>
                </>
              )}

              {/* Fields for NO */}
              {followUpForm.appointmentHappened === false && (
                <>
                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', marginBottom: '10px', display: 'block', color: '#d97706' }}>Reason</label>
                    <select 
                      value={followUpForm.reason}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, reason: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#e5e7eb' }}
                    >
                      <option value="">Select reason...</option>
                      <option value="Not interested">Not interested</option>
                      <option value="Too busy">Too busy</option>
                      <option value="Financial concerns">Financial concerns</option>
                      <option value="Need more time">Need more time</option>
                      <option value="Family emergency">Family emergency</option>
                      <option value="Technical issues">Technical issues</option>
                      <option value="No show">No show</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ fontSize: '16px', marginBottom: '10px', display: 'block', color: '#d97706' }}>Notes</label>
                    <textarea 
                      value={followUpForm.notesNo}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, notesNo: e.target.value })}
                      placeholder="Additional notes..."
                      rows="4"
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#e5e7eb' }}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <span style={{ fontSize: '16px', color: '#e5e7eb' }}>Would you like to </span>
                      <input 
                        type="checkbox" 
                        checked={followUpForm.scheduleAnother}
                        onChange={(e) => setFollowUpForm({ ...followUpForm, scheduleAnother: e.target.checked })}
                        style={{ width: 'auto', margin: '0 5px' }}
                      />
                      <span style={{ fontSize: '16px', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}>schedule another appointment</span>
                      <span style={{ fontSize: '16px', color: '#e5e7eb' }}>?</span>
                    </label>
                  </div>
                </>
              )}
            </div>
            <div className="matchup-modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowFollowUpModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleFollowUpSubmit}
                disabled={loading || followUpForm.appointmentHappened === null}
              >
                {loading ? 'Submitting...' : 'Submit Follow Up'}
              </button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
}

// Agent Search Modal Component
function AgentSearchModal({ open, onClose, onSelect, items }) {
  const [query, setQuery] = useState("");
  
  if (!open) return null;

  const filteredItems = items.filter((agent) =>
    agent.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Agent</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="agent-search">
          <input
            type="text"
            className="input"
            placeholder="Search agents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="agent-list">
            {filteredItems.map((agent) => (
              <div
                key={agent.id}
                className="agent-item"
                onClick={() => onSelect(agent.label, agent.id)}
              >
                {agent.label}
              </div>
            ))}
            {filteredItems.length === 0 && (
              <div className="agent-item" style={{ opacity: 0.6, cursor: "default" }}>
                No agents found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}